import type { CoreBus, IncomingSignal, OutgoingSignal, ServerConfig } from "./bus";
import { diffPeers, type PeerInfo } from "../lib/room-helpers";

export type RoomIdentity = { peerId: string; displayName: string; deviceName: string };

export type RoomConnectionDeps = {
  bus: CoreBus;
  identity: RoomIdentity;
  fetchFn: typeof fetch;
  sendBeaconFn: (url: string, body: Blob) => void;
  /** Prefix for api calls; "" yields relative "api/..." like the legacy app. */
  baseUrl?: string;
};

type SyncResponse = {
  iceServers?: RTCIceServer[];
  peers?: PeerInfo[];
  signals?: Array<IncomingSignal & { id: string }>;
};

const DEFAULT_ICE: RTCIceServer[] = [{ urls: "stun:stun.l.google.com:19302" }];

export class RoomConnection {
  private readonly bus: CoreBus;
  private readonly identity: RoomIdentity;
  private readonly fetchFn: typeof fetch;
  private readonly sendBeaconFn: (url: string, body: Blob) => void;
  private readonly base: string;

  private roomId: string | null = null;
  private _iceServers: RTCIceServer[] = DEFAULT_ICE;
  private knownPeers: PeerInfo[] = [];
  private outgoing: OutgoingSignal[] = [];
  private ackIds: string[] = [];
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private activeHandshakes = 0;
  private pollEpoch = 0;

  constructor(deps: RoomConnectionDeps) {
    this.bus = deps.bus;
    this.identity = deps.identity;
    this.fetchFn = deps.fetchFn;
    this.sendBeaconFn = deps.sendBeaconFn;
    this.base = deps.baseUrl ?? "";

    this.bus.on("signal-send", (s) => this.outgoing.push(s));
    this.bus.on("handshake-active", (active) => { this.activeHandshakes += active ? 1 : -1; });
    this.bus.on("pagehide", () => this.leave());
  }

  get peerId(): string { return this.identity.peerId; }
  iceServers(): RTCIceServer[] { return this._iceServers; }

  loadConfig(): Promise<void> {
    return this.fetchFn(`${this.base}api/config`)
      .then((r) => r.json())
      .then((cfg: ServerConfig) => this.bus.emit("config", cfg))
      .catch(() => this.bus.emit("config", { turnEnabled: false, buttons: {} }));
  }

  joinRoom(roomId: string): void {
    this.pollEpoch++;
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.roomId = roomId;
    this.knownPeers = [];
    void this.poll();
    this.scheduleNextPoll();
  }

  leaveRoom(): void {
    this.pollEpoch++;
    this.leave();
    this.roomId = null;
    this.knownPeers.forEach((p) => this.bus.emit("peer-left", { peerId: p.peerId }));
    this.knownPeers = [];
    if (this.pollTimer) clearTimeout(this.pollTimer);
  }

  private scheduleNextPoll(): void {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    const epoch = this.pollEpoch;
    const interval = this.activeHandshakes > 0 ? 2000 : 5000;
    this.pollTimer = setTimeout(() => {
      void this.poll().finally(() => {
        if (this.roomId && epoch === this.pollEpoch) this.scheduleNextPoll();
      });
    }, interval);
  }

  private async poll(): Promise<void> {
    if (!this.roomId) return;
    const outgoing = this.outgoing;
    const ack = this.ackIds;
    this.outgoing = [];
    this.ackIds = [];
    try {
      const res = await this.fetchFn(`${this.base}api/rooms/${encodeURIComponent(this.roomId)}/sync`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          peerId: this.identity.peerId,
          displayName: this.identity.displayName,
          deviceName: this.identity.deviceName,
          outgoing,
          ack,
        }),
      });
      if (!res.ok) throw new Error(`sync failed: ${res.status}`);
      const data = (await res.json()) as SyncResponse;
      if (Array.isArray(data.iceServers)) this._iceServers = data.iceServers;
      this.applyPeers(data.peers ?? []);
      this.applySignals(data.signals ?? []);
    } catch (e) {
      console.warn("RoomConnection: sync failed, requeueing signals", e);
      this.outgoing = outgoing.concat(this.outgoing);
      this.ackIds = ack.concat(this.ackIds);
    }
  }

  private applyPeers(peers: PeerInfo[]): void {
    const { joined, left } = diffPeers(this.knownPeers, peers);
    this.knownPeers = peers;
    joined.forEach((p) => this.bus.emit("peer-joined", { peer: p }));
    left.forEach((peerId) => this.bus.emit("peer-left", { peerId }));
  }

  private applySignals(signals: Array<IncomingSignal & { id: string }>): void {
    signals.forEach((s) => {
      this.ackIds.push(s.id);
      this.bus.emit("signal", { from: s.from, type: s.type, payload: s.payload });
    });
  }

  private leave(): void {
    if (!this.roomId) return;
    const body = JSON.stringify({ peerId: this.identity.peerId });
    this.sendBeaconFn(
      `${this.base}api/rooms/${encodeURIComponent(this.roomId)}/leave`,
      new Blob([body], { type: "application/json" }),
    );
  }
}
